/**
 * The frontend's shared-state primitive (round 63, owner ask during the Lit migration).
 *
 * The legacy pages "shared" state two ways, both of which the migration retires:
 * module-level `state` objects no other page could see or react to, and `window.*`
 * assignment - which was never a state system, only the one namespace an
 * `onclick="…"` attribute can resolve. Neither gives a consumer a way to KNOW a value
 * changed; every cross-surface refresh had to be hand-wired (reloadPage calls, custom
 * events) at the mutation site, which is exactly what rots.
 *
 * A store is one typed value with get/set/update and subscribe. Mutation happens
 * through the store, reaction happens through subscription, and the mutation site no
 * longer needs to know who is listening. Deliberately this small - no selectors, no
 * middleware, no global registry: a store is just a module export, so "which stores
 * exist" is answered by grep, and TypeScript owns the typing end to end.
 *
 * STYLEGUIDE.md ("State: passing, sharing, mutating") says which tier a given piece of
 * state belongs to - most state should NOT be a store: page-local state lives on the
 * island as reactive properties, and one-shot boot facts stay plain module reads.
 */
export interface Store<T> {
	get(): T;
	set(value: T): void;
	update(fn: (value: T) => T): void;
	/** Called on every change (not on subscribe); returns the unsubscribe function */
	subscribe(listener: (value: T) => void): () => void;
}

export function createStore<T>(initial: T): Store<T> {
	let value = initial;
	const listeners = new Set<(value: T) => void>();
	const set = (next: T): void => {
		if (Object.is(next, value)) return;
		value = next;
		// Snapshot so a listener unsubscribing (or subscribing) mid-notify can't skew iteration
		for (const listener of [...listeners]) listener(value);
	};
	return {
		get: () => value,
		set,
		update: fn => set(fn(value)),
		subscribe: listener => {
			listeners.add(listener);
			return () => listeners.delete(listener);
		}
	};
}
